/**
 * @file main.tsx
 * @description 前端應用入口。
 *
 * 使用 TanStack Router 的文件路由模式（File-based routing）：
 * - routeTree 由 vite-plugin-tanstack-router 根據 src/routes/ 自動生成到 routeTree.gen.ts
 * - RouterProvider 掛載 routeTree，__root.tsx 提供全局 Providers（ConfigProvider、QueryClient、AuthProvider）
 * - Service Worker 僅在生產環境啟動
 *
 * 注意：
 * - routeTree.gen.ts 由 Vite 插件自動生成，已加入 .gitignore，請勿手動編輯
 * - 舊版 pages/ 目錄仍保留（給遷移期舊頁面使用），但新的路由均在 routes/ 下
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { registerServiceWorker } from './pwa/sw-register';
import { installDatePickerClearOnEmpty } from '@/lib/date-picker-clear-on-empty';
/* ── 字體：@fontsource 本地加載，避免 Google Fonts 外部請求 ── */
import '@fontsource/onest/400.css';
import '@fontsource/onest/500.css';
import '@fontsource/onest/600.css';
import '@fontsource/onest/700.css';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/ibm-plex-mono/600.css';
import '@fontsource/noto-sans-tc/400.css';
import '@fontsource/noto-sans-tc/500.css';
import '@fontsource/noto-sans-tc/700.css';
import './styles/globals.css';

/**
 * 創建 TanStack Router 實例。
 * defaultPreload: 'intent' — 鼠標懸停時預加載路由塊，提升導航速度。
 */
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

/** 全局類型聲明，讓 TanStack Router hooks 能推斷路由類型 */
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// 序45（2026-09-25）：全站日期框「鍵盤刪光後失焦即清空」，修正刪除後日期又恢復的問題
installDatePickerClearOnEmpty();

const root = document.getElementById('root');
if (!root) throw new Error('Root element #root not found');

createRoot(root).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);

/**
 * Service Worker 僅在生產環境注冊。
 * Dev 模式下避免 SW 快取干擾 HMR。
 */
if (
  'serviceWorker' in navigator &&
  (import.meta.env.PROD || import.meta.env.VITE_PWA_DEV === '1')
) {
  registerServiceWorker();
}
